import React, { useState, useEffect } from 'react';
import { Alert, Collapse, Box, Typography, Button } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import apiClient from '../api/client';

const ConnectionStatus: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState<string>('');

  const checkConnection = async () => {
    try {
      // Try to access a public endpoint
      await apiClient.get('/');
      setIsConnected(true);
      setError('');
    } catch (err: any) {
      setIsConnected(false);
      
      if (err.message === 'Network Error' || !err.response) {
        setError('Cannot connect to backend server');
      } else if (err.response?.status === 404) {
        // 404 means the server is running but endpoint doesn't exist, which is ok
        setIsConnected(true);
        setError('');
      } else {
        setError(err.message || 'Connection error');
      }
    }
  };

  useEffect(() => {
    checkConnection();
    // Check connection every 30 seconds
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isConnected === null) {
    return null; // Still checking
  }

  if (isConnected) {
    return null; // Connected, don't show anything
  }

  return (
    <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, maxWidth: 400 }}>
      <Alert 
        severity="error" 
        icon={<ErrorIcon />}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide' : 'Details'}
          </Button>
        }
      >
        <Typography variant="body2" fontWeight="bold">
          Backend Connection Failed
        </Typography>
        <Typography variant="caption" display="block">
          {error || 'Cannot connect to the backend server'}
        </Typography>
      </Alert>
      
      <Collapse in={showDetails}>
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            How to fix this:
          </Typography>
          <Typography variant="caption" component="div">
            1. Make sure the backend server is running:
            <Box component="pre" sx={{ ml: 2, fontSize: '0.75rem' }}>
              cd book-recommender-backend{'\n'}
              uvicorn app.main:app --reload
            </Box>
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 1 }}>
            2. Check that it's running on port 8000
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 1 }}>
            3. If you see CORS errors, make sure the backend allows{' '}
            {window.location.origin}
          </Typography>
          <Button 
            size="small" 
            onClick={checkConnection} 
            sx={{ mt: 1 }}
          >
            Retry Connection
          </Button>
        </Alert>
      </Collapse>
    </Box>
  );
};

export default ConnectionStatus;